<template>
  <div class="tw:flex tw:flex-1 tw:flex-col">
    <template v-if="isAdminUser">
      <div class="tw:p-4">
        <div class="tw:mb-2 tw:font-bold">
          {{ getLocaleText("stream_filters_reports.tiers") }}
        </div>
        <div class="tw:flex tw:gap-4">
          <div
            v-for="tier in [1, 2, 3, 4, 5]"
            :key="tier"
          >
            <Checkbox
              v-model="tierValues"
              :data-testid="`option-tier-${tier}`"
              dense
              :label="getLocaleText('stream_filters_reports.tier', { tier })"
              new-design
              :value="tier"
            />
          </div>
        </div>
      </div>
      <BaseSeparator />
      <div class="tw:p-4">
        <div class="tw:mb-2 tw:font-bold">
          {{ getLocaleText("stream_filters_reports.sorting") }}
        </div>
        <SortingExpandable
          v-model="sortOptions"
          :excluded-sorting-options="excludedSortingOptions"
        />
      </div>
    </template>
    <BaseSeparator />
    <div class="tw:flex tw:flex-col tw:gap-3 tw:p-4">
      <QToggle
        v-model="includedMentionsEnabled"
        data-testid="included-mentions"
        dense
        :label="
          getLocaleText('content_expandable.hide_mentions_already_included')
        "
      />
      <QToggle
        v-if="isAdminUser && reportUpdated"
        v-model="lastSavedEnabled"
        data-testid="last-saved"
        dense
        :label="getLocaleText('content_expandable.filter_since_last_saved')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { QToggle } from "quasar";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseSeparator from "shared/components/base/BaseSeparator.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import SortingExpandable from "shared/components/selectors/expandables/SortingExpandable.vue";
import {
  type SortOptionField,
  excludedMediaItemSortFields,
  excludedTranscriptRequestSortFields,
} from "shared/helpers/mentions";
import {
  type StreamFiltersSortOption,
  type StreamFiltersTier,
} from "shared/helpers/StreamFilters";
import useUserStore from "shared/stores/user";
import { type Stream } from "shared/types";
import {
  isMediaRequestQuery,
  isTranscriptRequestQuery,
} from "shared/types/selectors";

interface StreamFiltersReportsProps {
  stream: Stream;
  reportUpdated?: boolean;
}

const userStore = useUserStore();
const { isAdminUser } = storeToRefs(userStore);

const props = defineProps<StreamFiltersReportsProps>();

const tiers = defineModel<StreamFiltersTier[]>("tiers", { default: () => [] });

const includedMentionsEnabled = defineModel<boolean>("includedMentionsEnabled");

const lastSavedEnabled = defineModel<boolean>("lastSavedEnabled");

const tierValues = computed({
  get() {
    return tiers.value.map((tier) => tier.value);
  },
  set(values) {
    tiers.value = values.map((value) => ({
      label: getLocaleText("stream_filters_reports.tier", { tier: value }),
      value,
    }));
  },
});

const sortOptions = defineModel<StreamFiltersSortOption[]>("sortOptions", {
  default: () => [],
});

const excludedSortingOptions = computed<SortOptionField[]>(() => {
  if (isMediaRequestQuery(props.stream) && props.stream.isMediaItems) {
    return excludedMediaItemSortFields;
  }

  if (
    isTranscriptRequestQuery(props.stream) &&
    props.stream.isTranscriptRequest
  ) {
    return excludedTranscriptRequestSortFields;
  }

  return [];
});
</script>
